<template>
  <div class="realm-marketplace-view">
    <!-- Header Banner -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card bg-gradient-dark border-0 shadow-lg text-white" style="background: linear-gradient(135deg, #1f1b2e 0%, #111827 100%); border-left: 4px solid #ff9900 !important; border-radius: 12px;">
          <div class="card-body p-3 p-md-4">
            <div class="row align-items-center">
              <div class="col-lg-8">
                <div class="d-flex flex-wrap align-items-center mb-2" style="gap: 8px;">
                  <span class="badge badge-warning text-dark font-weight-bold">
                    <i class="mdi mdi-cube-outline mr-1"></i> {{ $t('marketplace.badgeVirtual') }}
                  </span>
                  <span class="badge badge-outline-info">
                    <i class="mdi mdi-shield-check mr-1"></i> {{ $t('marketplace.badgeInstantPay') }}
                  </span>
                  <span class="badge badge-outline-success">
                    <i class="mdi mdi-check-decagram mr-1"></i> {{ $t('marketplace.badgeHris') }}
                  </span>
                </div>
                <h2 class="font-weight-bold mb-2 text-white">
                  {{ $t('marketplace.title') }}
                </h2>
                <p class="text-muted mb-0" style="font-size: 14px; line-height: 1.6;">
                  {{ $t('marketplace.subtitle') }}
                </p>
              </div>
              <div class="col-lg-4 text-lg-right mt-3 mt-lg-0">
                <button class="btn btn-warning font-weight-bold mr-2 mb-2 mb-sm-0" @click="openStudioModal">
                  <i class="mdi mdi-creation mr-1" aria-hidden="true"></i> {{ $t('marketplace.creatorStudioBtn') }}
                </button>
                <button class="btn btn-outline-light" @click="refreshAll" :title="$t('common.refresh')" :aria-label="$t('common.refresh')">
                  <i class="mdi mdi-refresh" aria-hidden="true"></i>
                </button>
              </div>
            </div>

            <!-- Tab Navigation (Marketplace vs Installed) -->
            <div class="d-flex mt-4 border-top border-secondary pt-3 flex-wrap" style="gap: 12px;">
              <button
                type="button"
                class="btn font-weight-bold px-4 py-2"
                :class="activeTab === 'catalog' ? 'btn-warning text-dark shadow-sm' : 'btn-outline-secondary text-white'"
                @click="activeTab = 'catalog'"
              >
                <i class="mdi mdi-storefront mr-1" aria-hidden="true"></i> {{ $t('marketplace.tabCatalog') }}
              </button>
              <button
                type="button"
                class="btn font-weight-bold px-4 py-2"
                :class="activeTab === 'installed' ? 'btn-warning text-dark shadow-sm' : 'btn-outline-secondary text-white'"
                @click="switchTabInstalled"
              >
                <i class="mdi mdi-shield-home mr-1" aria-hidden="true"></i> {{ $t('marketplace.tabInstalled') }}
                <span v-if="installedList.length > 0" class="badge badge-dark text-warning ml-1">
                  {{ installedList.length }}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Active Realm Notification Banner (If Any) -->
    <div v-if="activeRealm" class="alert alert-dark border border-warning d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 p-3 rounded shadow-sm" style="background: rgba(255, 153, 0, 0.08); gap: 12px;">
      <div class="d-flex align-items-center">
        <div class="p-2 rounded bg-warning text-dark mr-3 font-weight-bold d-flex align-items-center justify-content-center" style="width: 44px; height: 44px;">
          <i class="mdi mdi-check-decagram" style="font-size: 24px;" aria-hidden="true"></i>
        </div>
        <div>
          <h5 class="mb-0 text-white font-weight-bold">
            {{ $t('marketplace.activeRealmTitle') }} {{ activeRealm.realm ? activeRealm.realm.title : '-' }}
          </h5>
          <small class="text-muted">
            {{ $t('marketplace.activeRealmDeployed', { date: formatDate(activeRealm.installed_at) }) }}
          </small>
        </div>
      </div>
      <div class="d-flex" style="gap: 8px;">
        <router-link to="/rules/rewards" class="btn btn-sm btn-outline-warning">
          <i class="mdi mdi-format-list-checks mr-1" aria-hidden="true"></i> {{ $t('marketplace.manageQuestsBtn') }}
        </router-link>
      </div>
    </div>

    <!-- ============================================== -->
    <!-- TAB 1: MARKETPLACE CATALOG                     -->
    <!-- ============================================== -->
    <div v-if="activeTab === 'catalog'">
      <!-- Filters & Search Toolbar -->
      <div class="card mb-4 bg-dark border-secondary">
        <div class="card-body p-3">
          <div class="row align-items-center">
            <!-- Category Tabs -->
            <div class="col-lg-8 mb-3 mb-lg-0">
              <div class="d-flex flex-wrap" style="gap: 6px;">
                <button
                  v-for="cat in categories"
                  :key="cat.id"
                  type="button"
                  class="btn btn-sm"
                  :class="selectedCategory === cat.id ? 'btn-warning text-dark font-weight-bold' : 'btn-outline-secondary text-white'"
                  @click="filterCategory(cat.id)"
                >
                  <i :class="cat.icon" class="mr-1" aria-hidden="true"></i> {{ cat.name }}
                </button>
              </div>
            </div>

            <!-- Search Input -->
            <div class="col-lg-4">
              <div class="input-group">
                <input
                  type="text"
                  class="form-control form-control-sm bg-dark text-white border-secondary"
                  :placeholder="$t('marketplace.searchPlaceholder')"
                  :aria-label="$t('marketplace.searchPlaceholder')"
                  v-model="searchQuery"
                  @input="debounceSearch"
                />
                <div class="input-group-append">
                  <button class="btn btn-sm btn-warning" type="button" @click="fetchRealms" :aria-label="$t('common.search')">
                    <i class="mdi mdi-magnify" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-warning" role="status">
          <span class="sr-only">{{ $t('common.loading') }}</span>
        </div>
        <p class="text-muted mt-2">{{ $t('marketplace.loadingCatalog') }}</p>
      </div>

      <!-- Realms Cards Grid -->
      <div v-else class="row">
        <div v-if="realms.length === 0" class="col-12 text-center py-5">
          <i class="mdi mdi-emoticon-sad-outline text-muted" style="font-size: 48px;" aria-hidden="true"></i>
          <h4 class="text-white mt-2">{{ $t('marketplace.noRealmsFound') }}</h4>
          <p class="text-muted">{{ $t('marketplace.noRealmsDesc') }}</p>
        </div>

        <div
          v-for="realm in realms"
          :key="realm.id"
          class="col-md-6 col-xl-4 grid-margin stretch-card"
        >
          <div class="card realm-card h-100 bg-dark border-secondary position-relative">
            <!-- Banner Image & Badges -->
            <div class="realm-banner-wrapper position-relative" style="height: 160px; overflow: hidden; border-top-left-radius: 4px; border-top-right-radius: 4px;">
              <img
                :src="realm.banner_image || 'https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&w=800&q=80'"
                :alt="realm.title"
                class="w-100 h-100"
                style="object-fit: cover;"
              />
              <div class="position-absolute" style="top: 10px; left: 10px;">
                <span class="badge badge-dark text-warning border border-warning text-uppercase font-weight-bold">
                  {{ realm.theme }}
                </span>
              </div>
              <div class="position-absolute" style="top: 10px; right: 10px;">
                <span v-if="realm.is_currently_active" class="badge badge-success font-weight-bold">
                  <i class="mdi mdi-check-circle mr-1" aria-hidden="true"></i> {{ $t('marketplace.activeInWorkspaceBadge') }}
                </span>
                <span v-else-if="realm.is_purchased && !realm.is_free" class="badge badge-info font-weight-bold">
                  <i class="mdi mdi-certificate mr-1" aria-hidden="true"></i> {{ $t('marketplace.purchasedBadge') }}
                </span>
                <span v-else-if="realm.is_free" class="badge badge-success font-weight-bold">
                  {{ $t('marketplace.freeLicense') }}
                </span>
                <span v-else class="badge badge-warning text-dark font-weight-bold">
                  Rp {{ formatCurrencyNumber(realm.price) }}
                </span>
              </div>
            </div>

            <!-- Card Body -->
            <div class="card-body p-3 d-flex flex-column justify-content-between">
              <div>
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <small class="text-warning font-weight-bold text-uppercase">
                    <i class="mdi mdi-tag mr-1" aria-hidden="true"></i>{{ realm.category }}
                  </small>
                  <div class="small text-light">
                    <template v-if="Number(realm.rating) > 0">
                      <i class="mdi mdi-star text-warning" aria-hidden="true"></i>
                      <strong class="text-white ml-1">{{ Number(realm.rating).toFixed(1) }}</strong>
                    </template>
                    <span v-else class="text-muted">{{ $t('marketplace.unrated') }}</span>
                    <span class="text-muted ml-1">({{ realm.downloads_count || 0 }} {{ $t('marketplace.teamsUnit') }})</span>
                  </div>
                </div>

                <h4 class="card-title text-white font-weight-bold mb-2" style="font-size: 18px;">
                  {{ realm.title }}
                </h4>

                <p class="text-muted small mb-3" style="line-height: 1.5; min-height: 48px;">
                  {{ truncate(realm.description, 110) }}
                </p>

                <!-- Verified Impact Badge -->
                <div v-if="realm.verified_impact" class="p-2 rounded mb-3" style="background: rgba(0, 204, 136, 0.1); border: 1px solid rgba(0, 204, 136, 0.25);">
                  <small class="text-success font-weight-bold d-flex align-items-center">
                    <i class="mdi mdi-shield-check mr-1" style="font-size: 16px;" aria-hidden="true"></i>
                    {{ realm.verified_impact }}
                  </small>
                </div>
              </div>

              <!-- Card Actions -->
              <div class="pt-2 border-top border-secondary d-flex justify-content-between align-items-center">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-light"
                  @click="openPreview(realm)"
                >
                  <i class="mdi mdi-eye mr-1" aria-hidden="true"></i> {{ $t('common.view') }}
                </button>

                <!-- Action Button depends on pricing / purchased state -->
                <button
                  v-if="realm.is_currently_active"
                  type="button"
                  class="btn btn-sm btn-outline-success font-weight-bold"
                  disabled
                >
                  <i class="mdi mdi-check mr-1" aria-hidden="true"></i> {{ $t('common.active') }}
                </button>
                <button
                  v-else-if="realm.is_free || realm.is_purchased"
                  type="button"
                  class="btn btn-sm btn-warning font-weight-bold"
                  :disabled="deployingId === realm.id"
                  @click="deployRealm(realm)"
                >
                  <span v-if="deployingId === realm.id" class="spinner-border spinner-border-sm mr-1"></span>
                  <i v-else class="mdi mdi-rocket-launch mr-1" aria-hidden="true"></i>
                  {{ $t('marketplace.deployBtn') }}
                </button>
                <button
                  v-else
                  type="button"
                  class="btn btn-sm btn-warning font-weight-bold text-dark"
                  :disabled="deployingId === realm.id"
                  @click="deployRealm(realm)"
                >
                  <span v-if="deployingId === realm.id" class="spinner-border spinner-border-sm mr-1"></span>
                  <i v-else class="mdi mdi-cart-arrow-right mr-1" aria-hidden="true"></i>
                  {{ $t('marketplace.buyAndDeployBtn') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ============================================== -->
    <!-- TAB 2: INSTALLED REALMS & PURCHASE ORDERS      -->
    <!-- ============================================== -->
    <div v-else-if="activeTab === 'installed'">
      <!-- Active Realm Cards -->
      <div class="card bg-dark border-secondary mb-4">
        <div class="card-header border-secondary d-flex justify-content-between align-items-center">
          <h5 class="text-white font-weight-bold mb-0">
            <i class="mdi mdi-shield-check text-warning mr-1" aria-hidden="true"></i> {{ $t('marketplace.tabInstalled') }}
          </h5>
          <button class="btn btn-sm btn-outline-light" @click="fetchInstalledRealms">
            <i class="mdi mdi-refresh mr-1" aria-hidden="true"></i> {{ $t('common.refresh') }}
          </button>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-dark table-hover mb-0">
              <thead>
                <tr class="text-muted small">
                  <th>{{ $t('marketplace.colRealmSetting') }}</th>
                  <th>{{ $t('marketplace.colCategoryTheme') }}</th>
                  <th>{{ $t('marketplace.colInstalledDate') }}</th>
                  <th>{{ $t('common.status') }}</th>
                  <th>{{ $t('marketplace.action') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="installedList.length === 0">
                  <td colspan="5" class="text-center py-4">
                    <EmptyState
                      icon="mdi-rocket-launch-outline"
                      :title="$t('marketplace.noInstalledTitle')"
                      :description="$t('marketplace.noInstalledDesc')"
                      compact
                    />
                  </td>
                </tr>
                <tr v-for="inst in installedList" :key="inst.id">
                  <td>
                    <div class="d-flex align-items-center">
                      <div class="p-2 rounded bg-warning text-dark mr-2 font-weight-bold">
                        <i class="mdi mdi-gamepad-variant" aria-hidden="true"></i>
                      </div>
                      <div>
                        <strong class="text-white">{{ inst.realm ? inst.realm.title : ($t('marketplace.realmPrefix') + ' #' + inst.realm_id) }}</strong>
                        <div class="text-muted small">{{ inst.realm ? truncate(inst.realm.description, 60) : '-' }}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span class="badge badge-outline-warning mr-1 text-uppercase">{{ inst.realm ? inst.realm.category : $t('marketplace.generalCat') }}</span>
                    <span class="badge badge-dark border border-secondary text-uppercase">{{ inst.realm ? inst.realm.theme : $t('marketplace.standardTheme') }}</span>
                  </td>
                  <td class="text-light small">
                    {{ formatDate(inst.installed_at) }}
                  </td>
                  <td>
                    <span v-if="inst.is_active" class="badge badge-success">
                      <i class="mdi mdi-check-circle mr-1" aria-hidden="true"></i> {{ $t('common.active') }}
                    </span>
                    <span v-else class="badge badge-secondary">{{ $t('marketplace.archivedStatus') }}</span>
                  </td>
                  <td>
                    <button
                      v-if="!inst.is_active"
                      type="button"
                      class="btn btn-xs btn-outline-warning"
                      @click="deployRealm(inst.realm)"
                    >
                      <i class="mdi mdi-play mr-1" aria-hidden="true"></i> {{ $t('marketplace.reactivateBtn') }}
                    </button>
                    <router-link to="/rules/rewards" class="btn btn-xs btn-outline-info ml-1">
                      <i class="mdi mdi-eye mr-1" aria-hidden="true"></i> {{ $t('marketplace.manageQuestsBtn') }}
                    </router-link>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Purchase Orders -->
      <div class="card bg-dark border-secondary">
        <div class="card-header border-secondary">
          <h5 class="text-white font-weight-bold mb-0">
            <i class="mdi mdi-credit-card-check text-info mr-1" aria-hidden="true"></i> {{ $t('marketplace.purchaseHistoryTitle') }}
          </h5>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-dark table-hover mb-0">
              <thead>
                <tr class="text-muted small">
                  <th>{{ $t('marketplace.orderNumber') }}</th>
                  <th>{{ $t('marketplace.purchasedRealm') }}</th>
                  <th>{{ $t('marketplace.totalPrice') }}</th>
                  <th>{{ $t('marketplace.paymentStatus') }}</th>
                  <th>{{ $t('marketplace.paidDate') }}</th>
                  <th>{{ $t('marketplace.action') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="ordersList.length === 0">
                  <td colspan="6" class="text-center py-4">
                    <EmptyState
                      icon="mdi-cart-outline"
                      :title="$t('marketplace.noTransactions')"
                      :description="$t('marketplace.noTransactionsDesc')"
                      compact
                    />
                  </td>
                </tr>
                <tr v-for="ord in ordersList" :key="ord.id">
                  <td><code class="text-warning font-weight-bold">{{ ord.order_number }}</code></td>
                  <td><strong class="text-white">{{ ord.realm ? ord.realm.title : '-' }}</strong></td>
                  <td class="text-warning font-weight-bold">Rp {{ formatCurrencyNumber(ord.amount) }}</td>
                  <td>
                    <span v-if="ord.status === 'paid'" class="badge badge-success">
                      <i class="mdi mdi-check-all mr-1" aria-hidden="true"></i> {{ $t('marketplace.statusPaid') }}
                    </span>
                    <span v-else-if="ord.status === 'pending'" class="badge badge-warning text-dark">
                      <i class="mdi mdi-clock-outline mr-1" aria-hidden="true"></i> {{ $t('marketplace.statusPending') }}
                    </span>
                    <span v-else class="badge badge-danger">{{ ord.status }}</span>
                  </td>
                  <td class="text-light small">{{ formatDate(ord.paid_at || ord.created_at) }}</td>
                  <td>
                    <button
                      v-if="ord.status === 'pending'"
                      type="button"
                      class="btn btn-xs btn-warning text-dark font-weight-bold"
                      @click="resumePayment(ord)"
                    >
                      <i class="mdi mdi-cash mr-1" aria-hidden="true"></i> {{ $t('marketplace.payNow') }}
                    </button>
                    <span v-else class="text-muted small">{{ $t('marketplace.completed') }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- ============================================== -->
    <!-- PREVIEW MODAL                                  -->
    <!-- ============================================== -->
    <div
      v-if="previewRealm"
      class="modal fade show d-block"
      tabindex="-1"
      style="background: rgba(0,0,0,0.85); overflow-y: auto; z-index: 1050;"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content bg-dark text-white border-warning shadow-lg">
          <div class="modal-header border-secondary">
            <div class="d-flex align-items-center">
              <div class="p-2 rounded bg-warning text-dark mr-2">
                <i class="mdi mdi-gamepad-variant" style="font-size: 20px;" aria-hidden="true"></i>
              </div>
              <div>
                <h5 class="modal-title font-weight-bold text-white mb-0">{{ previewRealm.title }}</h5>
                <small class="text-muted">{{ $t('marketplace.categoryAndThemeMeta', { category: previewRealm.category, theme: previewRealm.theme }) }}</small>
              </div>
            </div>
            <button type="button" class="close text-white" @click="previewRealm = null" :aria-label="$t('marketplace.closeBtn')">
              <span>&times;</span>
            </button>
          </div>

          <div class="modal-body p-3 p-md-4">
            <!-- Price and verified impact callout -->
            <div class="d-flex justify-content-between align-items-center mb-3 p-3 rounded" style="background: rgba(255,255,255,0.04); border: 1px solid rgba(255,153,0,0.3);">
              <div>
                <span class="text-muted small">{{ $t('marketplace.licensePrice') }}</span>
                <h4 v-if="previewRealm.is_free" class="text-success font-weight-bold mb-0">{{ $t('marketplace.freeLicense') }}</h4>
                <h4 v-else class="text-warning font-weight-bold mb-0">Rp {{ formatCurrencyNumber(previewRealm.price) }}</h4>
              </div>
              <div v-if="previewRealm.verified_impact" class="text-right">
                <span class="badge badge-success px-2 py-1">
                  <i class="mdi mdi-shield-check mr-1" aria-hidden="true"></i> {{ previewRealm.verified_impact }}
                </span>
              </div>
            </div>

            <!-- Lore & Story -->
            <div class="mb-4 p-3 rounded" style="background: rgba(255, 255, 255, 0.03); border-left: 3px solid #ff9900;">
              <h6 class="text-warning font-weight-bold mb-1">
                <i class="mdi mdi-book-open-page-variant mr-1" aria-hidden="true"></i> {{ $t('marketplace.loreTitle') }}
              </h6>
              <p class="text-light small mb-0">
                {{ getLore(previewRealm) }}
              </p>
            </div>

            <!-- World Zones -->
            <div class="mb-4">
              <h6 class="text-white font-weight-bold mb-2">
                <i class="mdi mdi-map-marker-path text-info mr-1" aria-hidden="true"></i>
                {{ $t('marketplace.worldZonesTitle') }}
              </h6>
              <div class="row">
                <div
                  v-for="(zone, zIdx) in getZones(previewRealm)"
                  :key="zIdx"
                  class="col-sm-6 mb-2"
                >
                  <div class="p-2 rounded bg-secondary d-flex align-items-center">
                    <span class="badge badge-warning text-dark mr-2">Z{{ zIdx + 1 }}</span>
                    <span class="text-white small font-weight-bold">{{ zone.name }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Included Quests -->
            <div class="mb-4">
              <h6 class="text-white font-weight-bold mb-2">
                <i class="mdi mdi-format-list-checks text-success mr-1" aria-hidden="true"></i>
                {{ $t('marketplace.presetQuestsTitle') }}
              </h6>
              <div class="table-responsive">
                <table class="table table-sm table-dark">
                  <thead>
                    <tr class="text-muted small text-uppercase">
                      <th>{{ $t('marketplace.questNameCol') }}</th>
                      <th>{{ $t('marketplace.questTypeCol') }}</th>
                      <th>{{ $t('marketplace.questGoldCol') }}</th>
                      <th>{{ $t('marketplace.questXpCol') }}</th>
                      <th>{{ $t('marketplace.questBossDmgCol') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(q, qIdx) in getQuests(previewRealm)" :key="qIdx">
                      <td class="font-weight-bold text-light">{{ q.title }}</td>
                      <td><span class="badge badge-outline-info">{{ formatQuestType(q.type) }}</span></td>
                      <td class="text-warning font-weight-bold">+{{ q.points }}</td>
                      <td class="text-info">+{{ q.xp }}</td>
                      <td class="text-danger font-weight-bold"><i class="mdi mdi-sword-cross mr-1" aria-hidden="true"></i>{{ q.boss_dmg || 50 }} DMG</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Preconfigured Boss Raid -->
            <div class="mb-3 p-3 rounded" style="background: rgba(220, 53, 69, 0.1); border: 1px solid rgba(220, 53, 69, 0.3);">
              <div class="d-flex justify-content-between align-items-center flex-wrap" style="gap: 8px;">
                <div>
                  <h6 class="text-danger font-weight-bold mb-1">
                    <i class="mdi mdi-skull-crossbones mr-1" aria-hidden="true"></i>
                    {{ $t('marketplace.bossRaidTitle') }}: {{ getBoss(previewRealm).boss_name || $t('marketplace.defaultBossName') }}
                  </h6>
                  <small class="text-muted">{{ $t('marketplace.bossRaidSubtitle') }} (HP: {{ formatCurrencyNumber(getBoss(previewRealm).max_hp || 5000) }})</small>
                </div>
                <span class="badge badge-danger">{{ $t('marketplace.rewardPoolBadge') }}: {{ formatCurrencyNumber(getBoss(previewRealm).reward_gold_pool || 10000) }} Gold</span>
              </div>
            </div>
          </div>

          <div class="modal-footer border-secondary">
            <button type="button" class="btn btn-secondary" @click="previewRealm = null">{{ $t('marketplace.closeBtn') }}</button>
            <button
              type="button"
              class="btn btn-warning font-weight-bold"
              :disabled="deployingId === previewRealm.id"
              @click="deployRealm(previewRealm)"
            >
              <i class="mdi mdi-rocket-launch mr-1" aria-hidden="true"></i>
              {{ (previewRealm.is_free || previewRealm.is_purchased) ? $t('marketplace.deployNowBtn') : $t('marketplace.buyAndDeployBtn') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ============================================== -->
    <!-- XENDIT PAYMENT GATEWAY CHECKOUT MODAL          -->
    <!-- ============================================== -->
    <div
      v-if="checkoutModal"
      class="modal fade show d-block"
      tabindex="-1"
      style="background: rgba(0,0,0,0.85); overflow-y: auto; z-index: 1060;"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content bg-dark text-white border-warning shadow-lg">
          <div class="modal-header border-secondary">
            <div class="d-flex align-items-center">
              <div class="p-2 rounded bg-info text-white mr-2">
                <i class="mdi mdi-credit-card-outline" style="font-size: 20px;" aria-hidden="true"></i>
              </div>
              <div>
                <h5 class="modal-title font-weight-bold text-white mb-0">{{ $t('marketplace.checkoutTitle') }}</h5>
                <small class="text-muted">{{ $t('marketplace.checkoutSubtitle') }}</small>
              </div>
            </div>
            <button type="button" class="close text-white" @click="checkoutModal = null" :aria-label="$t('marketplace.cancelBtn')">
              <span>&times;</span>
            </button>
          </div>

          <div class="modal-body p-4 text-center">
            <div class="mb-3">
              <span class="badge badge-warning text-dark font-weight-bold px-3 py-1">
                {{ $t('marketplace.orderIdLabel') }}: {{ checkoutModal.order_number }}
              </span>
            </div>

            <h4 class="text-white font-weight-bold mb-1">{{ checkoutModal.realm.title }}</h4>
            <div class="display-4 font-weight-bold text-warning my-3" style="font-size: 32px;">
              Rp {{ formatCurrencyNumber(checkoutModal.amount) }}
            </div>

            <p class="text-muted small mb-4">
              {{ $t('marketplace.paymentDesc') }}
            </p>

            <div class="d-grid gap-2">
              <a
                v-if="checkoutModal.payment_url"
                :href="checkoutModal.payment_url"
                target="_blank"
                rel="noopener noreferrer"
                class="btn btn-warning btn-lg font-weight-bold text-dark w-100 mb-2 shadow-sm"
              >
                <i class="mdi mdi-open-in-new mr-1" aria-hidden="true"></i> {{ $t('marketplace.openPaymentUrl') }}
              </a>

              <button
                type="button"
                class="btn btn-outline-success btn-lg font-weight-bold w-100"
                :disabled="verifyingPayment"
                @click="verifyOrSimulatePayment(checkoutModal.order_id)"
              >
                <span v-if="verifyingPayment" class="spinner-border spinner-border-sm mr-1"></span>
                <i v-else class="mdi mdi-lightning-bolt mr-1" aria-hidden="true"></i>
                {{ $t('marketplace.simulatePayment') }}
              </button>
            </div>
          </div>

          <div class="modal-footer border-secondary justify-content-between">
            <small class="text-muted">{{ $t('marketplace.licensedGateway') }}</small>
            <button type="button" class="btn btn-sm btn-secondary" @click="checkoutModal = null">{{ $t('marketplace.cancelBtn') }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ============================================== -->
    <!-- STUDIO MODAL: ENTERPRISE BLUEPRINT FORGE       -->
    <!-- ============================================== -->
    <div
      v-if="studioModalOpen"
      class="modal fade show d-block"
      tabindex="-1"
      style="background: rgba(0,0,0,0.85); overflow-y: auto; z-index: 1050;"
    >
      <div class="modal-dialog modal-xl modal-dialog-centered">
        <div class="modal-content bg-dark text-white border-warning shadow-lg" style="border-radius: 14px;">
          <div class="modal-header border-secondary p-3 p-md-4">
            <div class="d-flex align-items-center">
              <div class="p-2 rounded bg-warning text-dark mr-3 font-weight-bold d-flex align-items-center justify-content-center" style="width: 44px; height: 44px;">
                <i class="mdi mdi-creation" style="font-size: 24px;" aria-hidden="true"></i>
              </div>
              <div>
                <h5 class="modal-title font-weight-bold text-white mb-0">
                  {{ $t('marketplace.studioTitle') }}
                </h5>
                <small class="text-muted">
                  {{ $t('marketplace.studioSubtitle') }}
                </small>
              </div>
            </div>
            <button type="button" class="close text-white" @click="studioModalOpen = false" :aria-label="$t('marketplace.cancelBtn')">
              <span>&times;</span>
            </button>
          </div>

          <div class="modal-body p-3 p-md-4">
            <!-- 1-Click Industry Presets Bar -->
            <div class="p-3 rounded mb-4" style="background: rgba(255, 153, 0, 0.06); border: 1px solid rgba(255, 153, 0, 0.25);">
              <label class="text-warning small font-weight-bold d-flex align-items-center mb-2">
                <i class="mdi mdi-flash mr-1" aria-hidden="true"></i> {{ $t('marketplace.presetSelectTitle') }}
              </label>
              <div class="d-flex flex-wrap" style="gap: 8px;">
                <button
                  v-for="preset in industryPresets"
                  :key="preset.id"
                  type="button"
                  class="btn btn-sm"
                  :class="activePresetId === preset.id ? 'btn-warning text-dark font-weight-bold shadow-sm' : 'btn-outline-secondary text-white'"
                  @click="applyIndustryPreset(preset)"
                >
                  <i :class="preset.icon + ' mr-1'" aria-hidden="true"></i>{{ preset.label }}
                </button>
              </div>
            </div>

            <!-- Gemini AI Formula Architect Box -->
            <div class="p-3 rounded mb-4" style="background: rgba(16, 185, 129, 0.08); border: 1px dashed rgba(16, 185, 129, 0.35);">
              <label class="text-white small font-weight-bold d-flex align-items-center mb-1">
                <i class="mdi mdi-robot text-success mr-1" aria-hidden="true"></i> {{ $t('marketplace.aiPromptLabel') }}
              </label>
              <textarea
                class="form-control bg-dark border-secondary text-white mb-2"
                rows="2"
                :placeholder="$t('marketplace.aiPromptPlaceholder')"
                v-model="aiGoalPrompt"
              ></textarea>
              <div class="d-flex justify-content-between align-items-center flex-wrap" style="gap: 8px;">
                <small class="text-muted">
                  {{ $t('marketplace.aiHelperText') }}
                </small>
                <button
                  type="button"
                  class="btn btn-sm btn-success text-dark font-weight-bold"
                  @click="generateBlueprintWithAi"
                  :disabled="isGeneratingAi"
                >
                  <span v-if="isGeneratingAi" class="spinner-border spinner-border-sm mr-1"></span>
                  <i v-else class="mdi mdi-creation mr-1" aria-hidden="true"></i>
                  {{ $t('marketplace.aiGenerateBtn') }}
                </button>
              </div>
            </div>

            <!-- Blueprint Core Configuration Form -->
            <form @submit.prevent="submitStudioRealm">
              <div class="row mb-3">
                <div class="col-md-6 mb-3 mb-md-0">
                  <label class="text-light font-weight-bold small">{{ $t('marketplace.blueprintTitleLabel') }} <span class="text-danger">*</span></label>
                  <input
                    type="text"
                    class="form-control bg-dark text-white border-secondary"
                    :placeholder="$t('marketplace.blueprintTitlePlaceholder')"
                    v-model="newRealm.title"
                    required
                  />
                </div>
                <div class="col-md-3 mb-3 mb-md-0">
                  <label class="text-light font-weight-bold small">{{ $t('marketplace.categoryLabel') }}</label>
                  <select class="form-control bg-dark text-white border-secondary" v-model="newRealm.category">
                    <option value="sales">{{ $t('marketplace.catSalesGuild') }}</option>
                    <option value="engineering">{{ $t('marketplace.catEngineeringSquad') }}</option>
                    <option value="support">{{ $t('marketplace.catSupportTeam') }}</option>
                    <option value="operations">{{ $t('marketplace.catOperationsTeam') }}</option>
                    <option value="culture">{{ $t('marketplace.catCultureTeam') }}</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <label class="text-light font-weight-bold small">{{ $t('marketplace.themeLabel') }}</label>
                  <select class="form-control bg-dark text-white border-secondary" v-model="newRealm.theme">
                    <option value="corporate">{{ $t('marketplace.themeCorporate') }}</option>
                    <option value="cyberpunk">{{ $t('marketplace.themeAgile') }}</option>
                    <option value="dojo">{{ $t('marketplace.themeDiscipline') }}</option>
                    <option value="fantasy">{{ $t('marketplace.themeRaid') }}</option>
                  </select>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-8 mb-3 mb-md-0">
                  <label class="text-light font-weight-bold small">{{ $t('marketplace.descLabel') }} <span class="text-danger">*</span></label>
                  <textarea
                    class="form-control bg-dark text-white border-secondary"
                    rows="2"
                    :placeholder="$t('marketplace.descPlaceholder')"
                    v-model="newRealm.description"
                    required
                  ></textarea>
                </div>
                <div class="col-md-4">
                  <label class="text-light font-weight-bold small">{{ $t('marketplace.priceLabel') }}</label>
                  <input
                    type="number"
                    min="0"
                    step="5000"
                    class="form-control bg-dark text-white border-secondary"
                    placeholder="0"
                    v-model.number="newRealm.price"
                  />
                  <small class="text-muted">{{ $t('marketplace.priceHelp') }}</small>
                </div>
              </div>

              <!-- Interactive Questline Builder Table -->
              <div class="mb-4 p-3 rounded" style="background: rgba(255, 255, 255, 0.02); border: 1px solid rgba(255, 255, 255, 0.08);">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h6 class="text-white font-weight-bold mb-0 d-flex align-items-center">
                    <i class="mdi mdi-format-list-checks text-warning mr-2" aria-hidden="true"></i>
                    {{ $t('marketplace.questsTableTitle') }} ({{ newRealm.quests.length }} {{ $t('marketplace.questsCountUnit') }})
                  </h6>
                  <button
                    type="button"
                    class="btn btn-xs btn-outline-warning font-weight-bold"
                    @click="addQuestRow"
                  >
                    {{ $t('marketplace.addQuestBtn') }}
                  </button>
                </div>

                <div class="table-responsive">
                  <table class="table table-sm table-dark table-borderless mb-0">
                    <thead>
                      <tr class="text-muted small border-bottom border-secondary">
                        <th style="min-width: 240px;">{{ $t('marketplace.questNameCol') }}</th>
                        <th style="width: 140px;">{{ $t('marketplace.questTypeCol') }}</th>
                        <th style="width: 100px;">{{ $t('marketplace.questGoldCol') }}</th>
                        <th style="width: 100px;">{{ $t('marketplace.questXpCol') }}</th>
                        <th style="width: 120px;">{{ $t('marketplace.questBossDmgCol') }}</th>
                        <th style="width: 50px;"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(quest, qIdx) in newRealm.quests" :key="qIdx" class="align-middle">
                        <td>
                          <input
                            type="text"
                            class="form-control form-control-sm bg-dark text-white border-secondary"
                            v-model="quest.title"
                            :placeholder="$t('marketplace.questRowPlaceholder')"
                            required
                          />
                        </td>
                        <td>
                          <select class="form-control form-control-sm bg-dark text-white border-secondary" v-model="quest.type">
                            <option value="daily">{{ $t('marketplace.typeDaily') }}</option>
                            <option value="weekly">{{ $t('marketplace.typeWeekly') }}</option>
                            <option value="sprint">{{ $t('marketplace.typeSprint') }}</option>
                          </select>
                        </td>
                        <td>
                          <input
                            type="number"
                            min="5"
                            class="form-control form-control-sm bg-dark text-warning border-secondary font-weight-bold"
                            v-model.number="quest.points"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            min="10"
                            class="form-control form-control-sm bg-dark text-info border-secondary"
                            v-model.number="quest.xp"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            min="10"
                            class="form-control form-control-sm bg-dark text-danger border-secondary font-weight-bold"
                            v-model.number="quest.boss_dmg"
                          />
                        </td>
                        <td class="text-center">
                          <button
                            type="button"
                            class="btn btn-xs btn-link text-danger p-0"
                            :title="$t('marketplace.removeQuestTitle')"
                            :aria-label="$t('marketplace.removeQuestTitle')"
                            :disabled="newRealm.quests.length <= 1"
                            @click="removeQuestRow(qIdx)"
                          >
                            <i class="mdi mdi-delete-outline" style="font-size: 18px;" aria-hidden="true"></i>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Boss Raid Parameters Section -->
              <div class="mb-4 p-3 rounded" style="background: rgba(239, 68, 68, 0.08); border: 1px solid rgba(239, 68, 68, 0.25);">
                <h6 class="text-danger font-weight-bold mb-2 d-flex align-items-center">
                  <i class="mdi mdi-skull-crossbones mr-2"></i>
                  {{ $t('marketplace.bossRaidSectionTitle') }}
                </h6>
                <div class="row">
                  <div class="col-md-6 mb-2 mb-md-0">
                    <label class="text-light small font-weight-bold">{{ $t('marketplace.bossNameLabel') }}</label>
                    <input
                      type="text"
                      class="form-control bg-dark text-white border-secondary"
                      :placeholder="$t('marketplace.bossNamePlaceholder')"
                      v-model="newRealm.boss_name"
                      required
                    />
                  </div>
                  <div class="col-md-3 mb-2 mb-md-0">
                    <label class="text-light small font-weight-bold">{{ $t('marketplace.bossHpLabel') }}</label>
                    <input
                      type="number"
                      min="500"
                      step="500"
                      class="form-control bg-dark text-danger font-weight-bold border-secondary"
                      v-model.number="newRealm.boss_hp"
                      required
                    />
                  </div>
                  <div class="col-md-3">
                    <label class="text-light small font-weight-bold">{{ $t('marketplace.bossRewardPoolLabel') }}</label>
                    <input
                      type="number"
                      min="1000"
                      step="1000"
                      class="form-control bg-dark text-warning font-weight-bold border-secondary"
                      v-model.number="newRealm.reward_gold_pool"
                      required
                    />
                  </div>
                </div>
              </div>

              <!-- Dual Action Buttons -->
              <div class="d-flex flex-column flex-sm-row justify-content-between align-items-center gap-2 pt-2 border-top border-secondary">
                <button type="button" class="btn btn-secondary px-4 w-100 w-sm-auto mb-2 mb-sm-0" @click="studioModalOpen = false">
                  {{ $t('marketplace.cancelBtn') }}
                </button>
                <div class="d-flex flex-wrap gap-2 w-100 w-sm-auto justify-content-end" style="gap: 8px;">
                  <button
                    type="button"
                    class="btn btn-outline-warning font-weight-bold"
                    :disabled="publishing || deployingDirect"
                    @click="submitStudioRealm"
                  >
                    <span v-if="publishing" class="spinner-border spinner-border-sm mr-1"></span>
                    <i v-else class="mdi mdi-earth mr-1"></i>
                    {{ $t('marketplace.publishMarketplaceBtn') }}
                  </button>

                  <button
                    type="button"
                    class="btn btn-warning font-weight-bold text-dark shadow-sm"
                    :disabled="publishing || deployingDirect"
                    @click="deployDirectlyToWorkspace"
                  >
                    <span v-if="deployingDirect" class="spinner-border spinner-border-sm mr-1"></span>
                    <i v-else class="mdi mdi-rocket-launch mr-1"></i>
                    {{ $t('marketplace.deployDirectBtn') }}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config/config';
import store from '@/store';
import Swal from 'sweetalert2';
import moment from 'moment';

const INDUSTRY_PRESETS_BY_LOCALE = {
  id: [
    {
      id: 'sales',
      icon: 'mdi mdi-crosshairs-gps',
      label: 'Penjualan B2B SaaS',
      category: 'sales',
      theme: 'cyberpunk',
      title: 'Kerangka Kerja Tim Penjualan B2B',
      description: 'Akselerasi kinerja tim komersial dari penjangkauan prospek hingga penyelesaian kontrak korporat dengan insentif Gold dan target Boss Raid kuartalan.',
      price: 0,
      boss_name: 'Target Pendapatan Kuartal (Rp 1,5 Miliar)',
      boss_hp: 10000,
      reward_gold_pool: 25000,
      quests: [
        { title: 'Panggilan Eksplorasi Harian (Min. 5 Prospek)', points: 25, xp: 75, boss_dmg: 100, type: 'daily' },
        { title: 'Presentasi Solusi Eksekutif (Min. 2 Sesi/Minggu)', points: 75, xp: 200, boss_dmg: 300, type: 'weekly' },
        { title: 'Penandatanganan Kontrak Klien Korporat', points: 250, xp: 750, boss_dmg: 1000, type: 'sprint' },
      ],
    },
    {
      id: 'engineering',
      icon: 'mdi mdi-code-braces',
      label: 'Rekayasa DevOps & Keandalan',
      category: 'engineering',
      theme: 'corporate',
      title: 'Cetak Biru Keandalan Sistem & DevOps',
      description: 'Tingkatkan keandalan sistem, kualitas peninjauan kode pull request, dan rilis produksi tanpa gangguan layanan.',
      price: 0,
      boss_name: 'Target Stabilitas Layanan Produksi',
      boss_hp: 8000,
      reward_gold_pool: 20000,
      quests: [
        { title: 'Peninjauan Kode Bersih Tanpa Temuan Kritis', points: 30, xp: 90, boss_dmg: 150, type: 'daily' },
        { title: 'Rilis Tanpa Gangguan & Uji Asap Otomatis', points: 60, xp: 180, boss_dmg: 250, type: 'weekly' },
        { title: 'Penyelesaian Bug Prioritas Sebelum Rilis', points: 120, xp: 350, boss_dmg: 500, type: 'sprint' },
      ],
    },
    {
      id: 'support',
      icon: 'mdi mdi-headset',
      label: 'Layanan Pelanggan Prima',
      category: 'support',
      theme: 'dojo',
      title: 'Standar Pengalaman & Layanan Pelanggan',
      description: 'Jaga kepuasan pelanggan melalui waktu tanggap cepat, penyelesaian tiket tuntas, dan standar komunikasi sopan.',
      price: 0,
      boss_name: 'Target Penyelesaian Antrean Tiket Bantuan',
      boss_hp: 7500,
      reward_gold_pool: 18000,
      quests: [
        { title: 'Tanggap Awal di Bawah 5 Menit', points: 20, xp: 60, boss_dmg: 80, type: 'daily' },
        { title: 'Penyelesaian Kontak Pertama dengan Penilaian Penuh', points: 50, xp: 150, boss_dmg: 200, type: 'weekly' },
        { title: 'Pembaruan Akun & Retensi Pelanggan Prioritas', points: 150, xp: 450, boss_dmg: 600, type: 'sprint' },
      ],
    },
    {
      id: 'operations',
      icon: 'mdi mdi-cart-check',
      label: 'Operasional Gerai & Ritel',
      category: 'operations',
      theme: 'dojo',
      title: 'Standar Operasional Gerai & Inventaris',
      description: 'Disiplin pembukaan gerai harian, rekonsiliasi kas akurat, penataan produk, dan pengendalian persediaan barang.',
      price: 0,
      boss_name: 'Target Pengendalian Selisih Persediaan',
      boss_hp: 6000,
      reward_gold_pool: 15000,
      quests: [
        { title: 'Daftar Periksa Pembukaan Gerai & Hitung Stok Pagi', points: 25, xp: 75, boss_dmg: 100, type: 'daily' },
        { title: 'Penutupan Kasir Harian Tanpa Selisih', points: 50, xp: 150, boss_dmg: 200, type: 'daily' },
        { title: 'Pemeriksaan Kerapian Penataan Produk Mingguan', points: 40, xp: 120, boss_dmg: 180, type: 'weekly' },
      ],
    },
    {
      id: 'culture',
      icon: 'mdi mdi-rocket-launch-outline',
      label: 'Orientasi Anggota Baru',
      category: 'culture',
      theme: 'fantasy',
      title: 'Cetak Biru Budaya & Orientasi 30 Hari',
      description: 'Percepat adaptasi karyawan baru dalam 30 hari pertama menjadi kontributor mandiri yang memahami standar kerja tim.',
      price: 0,
      boss_name: 'Target Kelulusan Orientasi Lintas Divisi',
      boss_hp: 5000,
      reward_gold_pool: 12500,
      quests: [
        { title: 'Penyiapan Perangkat Kerja & Kepatuhan Keamanan', points: 20, xp: 60, boss_dmg: 100, type: 'daily' },
        { title: 'Sesi Perkenalan Lintas Divisi', points: 35, xp: 100, boss_dmg: 150, type: 'weekly' },
        { title: 'Penyerahan Proyek Mandiri Pertama (Hari ke-30)', points: 200, xp: 600, boss_dmg: 800, type: 'sprint' },
      ],
    },
  ],
  en: [
    {
      id: 'sales',
      icon: 'mdi mdi-crosshairs-gps',
      label: 'B2B SaaS Sales Hunter',
      category: 'sales',
      theme: 'cyberpunk',
      title: 'SaaS B2B Revenue Growth Framework',
      description: 'Accelerate commercial execution from outbound discovery to enterprise contract closing with Gold incentives and quarterly Boss Raid targets.',
      price: 0,
      boss_name: 'Quarterly Revenue Target Titan',
      boss_hp: 10000,
      reward_gold_pool: 25000,
      quests: [
        { title: 'Daily Discovery Calls & Outreach (Min. 5 Prospects)', points: 25, xp: 75, boss_dmg: 100, type: 'daily' },
        { title: 'Executive Solution Demo Sessions (Min. 2/Week)', points: 75, xp: 200, boss_dmg: 300, type: 'weekly' },
        { title: 'Enterprise Contract Signing & Legal Closing', points: 250, xp: 750, boss_dmg: 1000, type: 'sprint' },
      ],
    },
    {
      id: 'engineering',
      icon: 'mdi mdi-code-braces',
      label: 'Zero-Defect DevOps & SRE',
      category: 'engineering',
      theme: 'corporate',
      title: 'Zero-Defect DevOps & Reliability Blueprint',
      description: 'Strengthen service reliability, pull request code review discipline, and zero-downtime deployment standards across engineering squads.',
      price: 0,
      boss_name: 'Production Service Reliability Citadel',
      boss_hp: 8000,
      reward_gold_pool: 20000,
      quests: [
        { title: 'Clean Code Review & Static Analysis Pass', points: 30, xp: 90, boss_dmg: 150, type: 'daily' },
        { title: 'Zero-Downtime Deployment & Automated Smoke Test', points: 60, xp: 180, boss_dmg: 250, type: 'weekly' },
        { title: 'Priority Staging Defect Resolution Before Release', points: 120, xp: 350, boss_dmg: 500, type: 'sprint' },
      ],
    },
    {
      id: 'support',
      icon: 'mdi mdi-headset',
      label: 'Customer Support Operations',
      category: 'support',
      theme: 'dojo',
      title: 'Customer Experience & Fast Resolution Blueprint',
      description: 'Maintain consistent customer satisfaction through rapid first response, accurate ticket resolution, and clear communication standards.',
      price: 0,
      boss_name: 'Support Backlog & Escalation Queue Target',
      boss_hp: 7500,
      reward_gold_pool: 18000,
      quests: [
        { title: 'Sub-5-Minute First Response Resolution', points: 20, xp: 60, boss_dmg: 80, type: 'daily' },
        { title: 'Full CSAT Score & First-Contact Resolution', points: 50, xp: 150, boss_dmg: 200, type: 'weekly' },
        { title: 'Priority Account Retention & Renewal Milestone', points: 150, xp: 450, boss_dmg: 600, type: 'sprint' },
      ],
    },
    {
      id: 'operations',
      icon: 'mdi mdi-cart-check',
      label: 'Retail & Store Operations',
      category: 'operations',
      theme: 'dojo',
      title: 'Omnichannel Store & Inventory Operations',
      description: 'Drive daily store opening discipline, accurate POS cash reconciliation, merchandising checks, and inventory accuracy.',
      price: 0,
      boss_name: 'Inventory Discrepancy Reduction Target',
      boss_hp: 6000,
      reward_gold_pool: 15000,
      quests: [
        { title: 'Morning Store Opening Checklist & Stock Count', points: 25, xp: 75, boss_dmg: 100, type: 'daily' },
        { title: 'Zero-Discrepancy Daily Cash & POS Closing', points: 50, xp: 150, boss_dmg: 200, type: 'daily' },
        { title: 'Weekly Merchandising & Display Standards Audit', points: 40, xp: 120, boss_dmg: 180, type: 'weekly' },
      ],
    },
    {
      id: 'culture',
      icon: 'mdi mdi-rocket-launch-outline',
      label: 'New Hire Onboarding',
      category: 'culture',
      theme: 'fantasy',
      title: '30-Day Team Culture & New Hire Onboarding',
      description: 'Guide new hires through their first 30 days into confident contributors aligned with operational standards and cross-team workflows.',
      price: 0,
      boss_name: 'Cross-Team Onboarding Graduation Milestone',
      boss_hp: 5000,
      reward_gold_pool: 12500,
      quests: [
        { title: 'Workstation Setup & Security Policy Checklist', points: 20, xp: 60, boss_dmg: 100, type: 'daily' },
        { title: 'Cross-Functional Team Alignment Session', points: 35, xp: 100, boss_dmg: 150, type: 'weekly' },
        { title: 'First Solo Project Delivery (30-Day Milestone)', points: 200, xp: 600, boss_dmg: 800, type: 'sprint' },
      ],
    },
  ],
};

export default {
  name: 'RealmMarketplaceView',
  data() {
    return {
      activeTab: 'catalog',
      loading: false,
      realms: [],
      installedList: [],
      ordersList: [],
      activeRealm: null,
      selectedCategory: 'all',
      searchQuery: '',
      previewRealm: null,
      checkoutModal: null,
      verifyingPayment: false,
      deployingId: null,
      studioModalOpen: false,
      publishing: false,
      deployingDirect: false,
      isGeneratingAi: false,
      aiGoalPrompt: '',
      activePresetId: 'sales',
      hasEditedStudioManually: false,
      newRealm: {
        title: '',
        category: 'sales',
        theme: 'cyberpunk',
        description: '',
        boss_name: '',
        boss_hp: 10000,
        reward_gold_pool: 25000,
        price: 0,
        quests: [],
      },
    };
  },
  computed: {
    industryPresets() {
      return this.$currentLocale === 'id' ? INDUSTRY_PRESETS_BY_LOCALE.id : INDUSTRY_PRESETS_BY_LOCALE.en;
    },
    categories() {
      return [
        { id: 'all', name: this.$t('marketplace.catAll'), icon: 'mdi-view-grid' },
        { id: 'sales', name: this.$t('marketplace.catSales'), icon: 'mdi-crosshairs-gps' },
        { id: 'engineering', name: this.$t('marketplace.catEngineering'), icon: 'mdi-code-braces' },
        { id: 'support', name: this.$t('marketplace.catSupport'), icon: 'mdi-headset' },
        { id: 'operations', name: this.$t('marketplace.catOperations'), icon: 'mdi-cart-check' },
        { id: 'culture', name: this.$t('marketplace.catCulture'), icon: 'mdi-account-group' },
      ];
    },
  },
  watch: {
    '$currentLocale'() {
      const currentPreset = this.industryPresets.find((p) => p.id === this.activePresetId) || this.industryPresets[0];
      if (!this.hasEditedStudioManually && currentPreset) {
        this.applyIndustryPreset(currentPreset);
      }
      this.fetchRealms();
    },
  },
  mounted() {
    if (this.industryPresets[0]) {
      this.applyIndustryPreset(this.industryPresets[0]);
    }
    this.fetchRealms();
    this.fetchActiveInstallation();
    this.fetchInstalledRealms();
  },
  methods: {
    formatCurrencyNumber(val) {
      return Number(val || 0).toLocaleString(this.$currentLocale === 'id' ? 'id-ID' : 'en-US');
    },
    formatQuestType(type) {
      const key = String(type || 'daily').toLowerCase();
      if (key === 'weekly') return this.$t('marketplace.typeWeekly');
      if (key === 'sprint' || key === 'milestone') return this.$t('marketplace.typeSprint');
      return this.$t('marketplace.typeDaily');
    },
    formatDate(date) {
      if (!date) return '-';
      return moment(date).format('DD MMM YYYY, HH:mm');
    },
    truncate(str, length) {
      if (!str) return '';
      return str.length > length ? str.substring(0, length) + '...' : str;
    },
    refreshAll() {
      this.fetchRealms();
      this.fetchActiveInstallation();
      this.fetchInstalledRealms();
    },
    switchTabInstalled() {
      this.activeTab = 'installed';
      this.fetchInstalledRealms();
    },
    filterCategory(catId) {
      this.selectedCategory = catId;
      this.fetchRealms();
    },
    debounceSearch() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.fetchRealms();
      }, 350);
    },
    fetchRealms() {
      this.loading = true;
      const API_URL = config.API_URL;
      const params = {};
      if (this.selectedCategory !== 'all') {
        params.category = this.selectedCategory;
      }
      if (this.searchQuery) {
        params.search = this.searchQuery;
      }

      const token = store.state.authToken || localStorage.getItem('authToken') || localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      axios
        .get(`${API_URL}realms/marketplace`, { params, headers })
        .then((res) => {
          this.realms = res.data.data.data || res.data.data || [];
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    fetchActiveInstallation() {
      const API_URL = config.API_URL;
      const token = store.state.authToken || localStorage.getItem('authToken') || localStorage.getItem('token');
      if (!token) return;

      axios
        .get(`${API_URL}owner/realms/active`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          if (res.data && res.data.data) {
            this.activeRealm = res.data.data;
          }
        })
        .catch(() => {});
    },
    fetchInstalledRealms() {
      const API_URL = config.API_URL;
      const token = store.state.authToken || localStorage.getItem('authToken') || localStorage.getItem('token');
      if (!token) return;

      axios
        .get(`${API_URL}owner/realms/installed`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          if (res.data && res.data.data) {
            this.installedList = res.data.data.installations || [];
            this.ordersList = res.data.data.orders || [];
          }
        })
        .catch(() => {});
    },
    openPreview(realm) {
      this.previewRealm = realm;
    },
    deployRealm(realm) {
      const API_URL = config.API_URL;
      const token = store.state.authToken || localStorage.getItem('authToken') || localStorage.getItem('token');
      const isId = this.$currentLocale === 'id';
      if (!token) {
        Swal.fire({
          icon: 'warning',
          title: isId ? 'Perlu Login' : 'Login Required',
          text: isId ? 'Silakan login sebagai Game Owner/Manager untuk mendeploy realm.' : 'Please login as Game Owner/Manager to deploy this realm.',
        });
        return;
      }

      this.deployingId = realm.id;
      axios
        .post(
          `${API_URL}owner/realms/${realm.id}/deploy`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        )
        .then((res) => {
          this.deployingId = null;

          // Check if payment is required via Xendit
          if (res.data.status === 'payment_required') {
            this.previewRealm = null;
            this.checkoutModal = res.data.data;
            return;
          }

          // Direct deploy success
          this.previewRealm = null;
          Swal.fire({
            icon: 'success',
            title: this.$t('marketplace.deployDirectSuccess'),
            html: isId
              ? `<strong>${realm.title}</strong> kini aktif di workspace tim Anda.<br>Seluruh misi dan Boss Raid tim telah disiapkan.`
              : `<strong>${realm.title}</strong> is now active in your workspace.<br>All team quests and Boss Raid parameters have been prepared.`,
            confirmButtonColor: '#ff9900',
          });
          this.fetchActiveInstallation();
          this.fetchRealms();
          this.fetchInstalledRealms();
        })
        .catch((err) => {
          this.deployingId = null;
          const msg = err.response?.data?.message || (isId ? 'Gagal mendeploy realm.' : 'Failed to deploy realm.');
          Swal.fire({
            icon: 'error',
            title: this.$t('common.error'),
            text: msg,
          });
        });
    },
    resumePayment(order) {
      this.checkoutModal = {
        order_id: order.id,
        order_number: order.order_number,
        amount: order.amount,
        payment_url: order.xendit_invoice_url,
        realm: order.realm || { title: 'Game Realm Setting' },
      };
    },
    verifyOrSimulatePayment(orderId) {
      const API_URL = config.API_URL;
      const token = store.state.authToken || localStorage.getItem('authToken') || localStorage.getItem('token');
      if (!token) return;

      this.verifyingPayment = true;
      const isId = this.$currentLocale === 'id';
      axios
        .post(
          `${API_URL}owner/realms/orders/${orderId}/verify?simulate=1`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        )
        .then((res) => {
          this.verifyingPayment = false;
          this.checkoutModal = null;
          Swal.fire({
            icon: 'success',
            title: isId ? 'Pembayaran Lunas & Realm Aktif!' : 'Payment Complete & Realm Activated!',
            text: res.data.message || (isId ? 'Game Realm berhasil dideploy ke workspace!' : 'Game Realm deployed to workspace successfully!'),
            confirmButtonColor: '#ff9900',
          });
          this.fetchRealms();
          this.fetchActiveInstallation();
          this.fetchInstalledRealms();
        })
        .catch((err) => {
          this.verifyingPayment = false;
          Swal.fire({
            icon: 'error',
            title: isId ? 'Verifikasi Gagal' : 'Verification Failed',
            text: err.response?.data?.message || (isId ? 'Gagal memverifikasi status pembayaran.' : 'Failed to verify payment status.'),
          });
        });
    },
    openStudioModal() {
      this.studioModalOpen = true;
    },
    applyIndustryPreset(preset) {
      this.activePresetId = preset.id;
      this.hasEditedStudioManually = false;
      this.newRealm.title = preset.title;
      this.newRealm.category = preset.category;
      this.newRealm.theme = preset.theme;
      this.newRealm.description = preset.description;
      this.newRealm.price = preset.price;
      this.newRealm.boss_name = preset.boss_name;
      this.newRealm.boss_hp = preset.boss_hp;
      this.newRealm.reward_gold_pool = preset.reward_gold_pool;
      this.newRealm.quests = JSON.parse(JSON.stringify(preset.quests));
    },
    addQuestRow() {
      this.hasEditedStudioManually = true;
      this.newRealm.quests.push({
        title: this.$t('marketplace.newQuestDefaultTitle'),
        points: 25,
        xp: 75,
        boss_dmg: 100,
        type: 'daily',
      });
    },
    removeQuestRow(index) {
      if (this.newRealm.quests.length > 1) {
        this.hasEditedStudioManually = true;
        this.newRealm.quests.splice(index, 1);
      }
    },
    async generateBlueprintWithAi() {
      const isId = this.$currentLocale === 'id';
      if (!this.aiGoalPrompt.trim()) {
        Swal.fire({
          icon: 'warning',
          title: isId ? 'Input Sasaran Kosong' : 'Empty Target Input',
          text: isId ? 'Tuliskan sasaran kerja atau target SOP yang ingin dirancang dengan AI.' : 'Please describe the work target or SOP goal to design with AI.',
          confirmButtonColor: '#ff9900',
        });
        return;
      }

      this.isGeneratingAi = true;
      this.hasEditedStudioManually = true;
      const API_URL = config.API_URL;
      const token = store.state.authToken || localStorage.getItem('authToken') || localStorage.getItem('token');

      try {
        const res = await axios.post(
          `${API_URL}ai/generate-rules`,
          {
            goal: this.aiGoalPrompt,
            division: this.newRealm.category,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.data?.data?.rules && res.data.data.rules.length > 0) {
          const rules = res.data.data.rules;
          this.newRealm.title = isId
            ? `Formula AI: ${this.aiGoalPrompt.slice(0, 40)}`
            : `AI Formula: ${this.aiGoalPrompt.slice(0, 40)}`;
          this.newRealm.description = isId
            ? `Formula kinerja terstruktur dari sasaran: "${this.aiGoalPrompt}". Menyelaraskan target SOP harian dan kolaborasi tim.`
            : `Structured performance blueprint synthesized from goal: "${this.aiGoalPrompt}". Aligns daily SOP milestones and team collaboration.`;
          this.newRealm.quests = rules.map((r, idx) => ({
            title: r.name || (isId ? `Target Misi ${idx + 1}` : `Quest Target ${idx + 1}`),
            points: r.gold || 30,
            xp: (r.gold || 30) * 3,
            boss_dmg: (r.gold || 30) * 4,
            type: r.type === 'daily' ? 'daily' : 'weekly',
          }));
        } else {
          this.synthesizeSmartFallback();
        }
      } catch (e) {
        this.synthesizeSmartFallback();
      } finally {
        this.isGeneratingAi = false;
        Swal.fire({
          icon: 'success',
          title: isId ? 'Formula AI Berhasil Dirancang' : 'AI Formula Designed',
          text: isId
            ? 'Rangkaian misi, poin hadiah, dan parameter Boss Raid telah disesuaikan dengan sasaran Anda.'
            : 'Quest sequences, reward points, and Boss Raid parameters have been aligned with your goals.',
          confirmButtonColor: '#ff9900',
          timer: 2000,
        });
      }
    },
    synthesizeSmartFallback() {
      const isId = this.$currentLocale === 'id';
      const p = this.aiGoalPrompt.trim();
      this.newRealm.title = isId ? `Kerangka Kerja: ${p.slice(0, 45)}` : `Framework: ${p.slice(0, 45)}`;
      this.newRealm.description = isId
        ? `Cetak biru terstruktur dari sasaran: "${p}". Mengintegrasikan daftar periksa mutu SOP dan target kolaborasi tim.`
        : `Structured blueprint synthesized from goal: "${p}". Combines SOP quality checklists with shared raid milestones.`;
      this.newRealm.boss_name = isId ? `Target Kolektif: ${p.slice(0, 30)}` : `Collective Target: ${p.slice(0, 30)}`;
      this.newRealm.boss_hp = 8000;
      this.newRealm.reward_gold_pool = 20000;
      this.newRealm.quests = isId
        ? [
            { title: `Pelaksanaan Harian SOP: ${p.slice(0, 35)}`, points: 25, xp: 75, boss_dmg: 100, type: 'daily' },
            { title: 'Verifikasi Kualitas & Evaluasi Mingguan', points: 60, xp: 180, boss_dmg: 250, type: 'weekly' },
            { title: 'Penyelesaian Target Utama Periode', points: 150, xp: 450, boss_dmg: 600, type: 'sprint' },
          ]
        : [
            { title: `Daily SOP Execution: ${p.slice(0, 35)}`, points: 25, xp: 75, boss_dmg: 100, type: 'daily' },
            { title: 'Weekly Quality Verification Milestone', points: 60, xp: 180, boss_dmg: 250, type: 'weekly' },
            { title: 'High-Impact Period Target Delivery', points: 150, xp: 450, boss_dmg: 600, type: 'sprint' },
          ];
    },
    getBlueprintPayload() {
      const isId = this.$currentLocale === 'id';
      return {
        title: this.newRealm.title,
        category: this.newRealm.category,
        theme: this.newRealm.theme,
        description: this.newRealm.description,
        price: this.newRealm.price,
        configuration_json: {
          lore: isId
            ? `Kerangka kerja terpadu untuk tim ${this.newRealm.category} mencapai target operasional.`
            : `Integrated operational framework for the ${this.newRealm.category} division.`,
          world_zones: isId
            ? [
                { id: 'z1', name: 'Zona Disiplin Harian' },
                { id: 'z2', name: 'Arena Pencapaian Mingguan' },
                { id: 'z3', name: 'Benteng Target Kolektif' },
              ]
            : [
                { id: 'z1', name: 'Daily Habit Zone' },
                { id: 'z2', name: 'Milestone Arena' },
                { id: 'z3', name: 'Boss Citadel' },
              ],
          quests: this.newRealm.quests.map((q) => ({
            title: q.title,
            points: Number(q.points) || 25,
            xp: Number(q.xp) || 75,
            boss_dmg: Number(q.boss_dmg) || 100,
            type: q.type || 'daily',
          })),
          boss_raid_preset: {
            boss_name: this.newRealm.boss_name || this.$t('marketplace.defaultBossName'),
            max_hp: Number(this.newRealm.boss_hp) || 8000,
            reward_gold_pool: Number(this.newRealm.reward_gold_pool) || 20000,
          },
        },
      };
    },
    submitStudioRealm() {
      const API_URL = config.API_URL;
      const token = store.state.authToken || localStorage.getItem('authToken') || localStorage.getItem('token');
      if (!token) return;

      this.publishing = true;
      const payload = this.getBlueprintPayload();
      const isId = this.$currentLocale === 'id';

      axios
        .post(`${API_URL}owner/realms/publish`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(() => {
          this.publishing = false;
          this.studioModalOpen = false;
          Swal.fire({
            icon: 'success',
            title: this.$t('marketplace.publishSuccess'),
            text: this.$t('marketplace.publishSuccessMsg'),
            confirmButtonColor: '#ff9900',
          });
          this.fetchRealms();
        })
        .catch((err) => {
          this.publishing = false;
          const msg = err.response?.data?.message || (isId ? 'Gagal mempublikasikan setting.' : 'Failed to publish game setting.');
          Swal.fire({
            icon: 'error',
            title: this.$t('common.error'),
            text: msg,
          });
        });
    },
    async deployDirectlyToWorkspace() {
      const API_URL = config.API_URL;
      const token = store.state.authToken || localStorage.getItem('authToken') || localStorage.getItem('token');
      const isId = this.$currentLocale === 'id';
      if (!token) {
        Swal.fire({
          icon: 'warning',
          title: isId ? 'Perlu Login' : 'Login Required',
          text: isId ? 'Silakan login sebagai Game Owner/Manager untuk mendeploy realm.' : 'Please login as Game Owner/Manager to deploy this realm.',
        });
        return;
      }

      this.deployingDirect = true;
      const payload = this.getBlueprintPayload();

      try {
        // 1. Publish Blueprint
        const pubRes = await axios.post(`${API_URL}owner/realms/publish`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const createdRealm = pubRes.data?.data;
        if (!createdRealm || !createdRealm.id) {
          throw new Error(isId ? 'Gagal mendapatkan ID realm baru.' : 'Failed to retrieve new realm ID.');
        }

        // 2. Deploy Immediately to workspace
        await axios.post(
          `${API_URL}owner/realms/${createdRealm.id}/deploy`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );

        this.deployingDirect = false;
        this.studioModalOpen = false;

        Swal.fire({
          icon: 'success',
          title: this.$t('marketplace.deployDirectSuccess'),
          html: isId
            ? `<strong>${this.newRealm.title}</strong> berhasil dipasang!<br>Seluruh ${this.newRealm.quests.length} misi dan Boss Raid tim telah aktif di workspace Anda.`
            : `<strong>${this.newRealm.title}</strong> successfully installed!<br>All ${this.newRealm.quests.length} quests and Boss Raid parameters are now active in your workspace.`,
          confirmButtonColor: '#ff9900',
        });

        this.fetchActiveInstallation();
        this.fetchRealms();
        this.fetchInstalledRealms();
      } catch (err) {
        this.deployingDirect = false;
        const msg = err.response?.data?.message || err.message || (isId ? 'Gagal mendeploy blueprint ke workspace.' : 'Failed to deploy blueprint to workspace.');
        Swal.fire({
          icon: 'error',
          title: isId ? 'Gagal Deploy' : 'Deploy Failed',
          text: msg,
        });
      }
    },
    getLore(realm) {
      if (!realm || !realm.configuration_json) return '';
      const cfg = typeof realm.configuration_json === 'string' ? JSON.parse(realm.configuration_json) : realm.configuration_json;
      return cfg.lore || realm.description;
    },
    getZones(realm) {
      if (!realm || !realm.configuration_json) return [];
      const cfg = typeof realm.configuration_json === 'string' ? JSON.parse(realm.configuration_json) : realm.configuration_json;
      return cfg.world_zones || [];
    },
    getQuests(realm) {
      if (!realm || !realm.configuration_json) return [];
      const cfg = typeof realm.configuration_json === 'string' ? JSON.parse(realm.configuration_json) : realm.configuration_json;
      return cfg.quests || [];
    },
    getBoss(realm) {
      if (!realm || !realm.configuration_json) return {};
      const cfg = typeof realm.configuration_json === 'string' ? JSON.parse(realm.configuration_json) : realm.configuration_json;
      return cfg.boss_raid_preset || {};
    },
  },
};
</script>

<style scoped>
.realm-card {
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  border-radius: 8px;
}
.realm-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.45);
  border-color: #ff9900 !important;
}
.btn-xs {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  line-height: 1.5;
  border-radius: 0.2rem;
}
</style>
